import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { encryptPassword, getBrowserInfo, getOsInfo } from '@/lib/crypto';
import { renderGoogleButton } from '@/lib/google';
import AuthLegalConsent from '@/components/auth/AuthLegalConsent';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [verToken, setVerToken] = useState('');
  const [captchaImg, setCaptchaImg] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeLogin, consumeRedirect, isAuthenticated } = useAuth();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const loadLoginPrerequisites = async () => {
    const [website, code] = await Promise.all([api.getWebsiteInfo(), api.getVerCode()]);
    setPublicKey(website.rsaLoginPublicKey || '');
    setVerToken(code.verToken);
    setCaptchaImg(code.img);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account', { replace: true });
      return;
    }
    void loadLoginPrerequisites().catch((requestError) => {
      setError(requestError instanceof Error ? requestError.message : '初始化失敗');
    });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!googleButtonRef.current || isAuthenticated) {
      return;
    }

    let active = true;
    setGoogleLoading(true);
    void renderGoogleButton({
      container: googleButtonRef.current,
      text: 'signin_with',
      onCredential: async (googleToken) => {
        if (!active) {
          return;
        }
        if (!agreed) {
          setError('請先閱讀並同意服務條款、私隱政策與退款政策');
          setGoogleLoading(false);
          return;
        }
        setGoogleLoading(true);
        setError('');
        try {
          const result = await api.googleLogin({ googleToken });
          await completeLogin(result);
          const redirect = searchParams.get('redirect') || consumeRedirect() || '/account';
          navigate(redirect, { replace: true });
        } catch (requestError) {
          setError(requestError instanceof Error ? requestError.message : 'Google 登入失敗');
          setGoogleLoading(false);
        }
      },
      onError: (requestError) => {
        if (!active) {
          return;
        }
        setError(requestError.message);
        setGoogleLoading(false);
      }
    })
      .catch((requestError) => {
        if (!active) {
          return;
        }
        setError(requestError instanceof Error ? requestError.message : 'Google 登入初始化失敗');
      })
      .finally(() => {
        if (active) {
          setGoogleLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [agreed, completeLogin, consumeRedirect, isAuthenticated, navigate, searchParams]);

  const handleEmailLogin = (e: React.FormEvent) => {
    e.preventDefault();
    void (async () => {
      if (!email || !password || !captcha) {
        setError('請填寫信箱、密碼與驗證碼');
        return;
      }
      if (!agreed) {
        setError('請先閱讀並同意服務條款、私隱政策與退款政策');
        return;
      }
      if (!publicKey || !verToken) {
        setError('驗證碼尚未準備完成，請刷新後重試');
        return;
      }

      setLoading(true);
      setError('');
      try {
        const mobilePwdEncrypt = encryptPassword(password, publicKey);
        const result = await api.login({
          email,
          mobilePwdEncrypt,
          verToken,
          verCode: captcha,
          os: getOsInfo(),
          browser: getBrowserInfo().name
        });
        await completeLogin(result);
        const redirect = searchParams.get('redirect') || consumeRedirect() || '/account';
        navigate(redirect, { replace: true });
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '登入失敗');
        setCaptcha('');
        await loadLoginPrerequisites();
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #7c3aed 100%)' }}
      >
        <img
          src="/assets/readdy/login-bg-01-900x1080-portrait-36ed2a46.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/60 via-transparent to-purple-900/60" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3 cursor-pointer w-fit">
            <img
              src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
              alt="NexusGo"
              className="w-10 h-10 rounded-xl object-cover"
            />
            <span className="text-white font-bold text-xl tracking-tight">NexusGo</span>
          </Link>

          <div className="space-y-6">
            <div className="w-14 h-1 bg-violet-300 rounded-full" />
            <h2 className="text-4xl font-bold text-white leading-snug">
              連結全球頂尖導師
              <br />
              加速你的職業成長
            </h2>
            <p className="text-violet-200 text-base leading-relaxed max-w-sm">
              登入後會直接同步真實後端帳號、課程、訂單與個人資料。
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[420px]">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-1.5">歡迎回來</h1>
            <p className="text-slate-500 text-sm">
              還沒有帳號？{' '}
              <Link to="/register" className="text-violet-600 font-medium hover:text-violet-700 cursor-pointer">
                免費註冊
              </Link>
            </p>
          </div>

          <div className="mb-5">
            <div
              ref={googleButtonRef}
              className={`min-h-11 transition-opacity ${googleLoading ? 'opacity-70' : 'opacity-100'}`}
            />
            {googleLoading && (
              <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-500">
                <i className="ri-loader-4-line animate-spin" />
                正在初始化 Google 登入
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-slate-400 text-xs">或使用信箱登入</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-xl mb-4">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-500 text-sm" />
              </div>
              <div className="text-xs text-amber-800 leading-relaxed">{error}</div>
            </div>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">信箱地址</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">密碼</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="輸入密碼"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">圖形驗證碼</label>
              <div className="flex items-center gap-2.5">
                <input
                  type="text"
                  value={captcha}
                  onChange={(event) => setCaptcha(event.target.value)}
                  placeholder="輸入 4 位驗證碼"
                  maxLength={4}
                  className="min-w-0 flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm tracking-[0.25em] text-slate-900 placeholder:tracking-normal placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => void loadLoginPrerequisites()}
                  className="w-28 h-[42px] shrink-0 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center hover:border-violet-300 transition-colors"
                >
                  {captchaImg ? (
                    <img src={captchaImg} alt="captcha" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-slate-400">載入中...</span>
                  )}
                </button>
              </div>
            </div>

            <AuthLegalConsent checked={agreed} onChange={setAgreed} compact />

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  登入中...
                </>
              ) : (
                '立即登入'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
